import type {
  GroupWorksheetCreationBody,
  WorksheetCreationBody,
} from '@mathflat/domain/MakeWorksheet'
import type { PartitionType } from '@mathflat/domain/PrintDocument'
import type { Revision, SchoolType } from '@mathflat/domain/SchoolSystem'
import { computed, makeObservable, observable, reaction, runInAction } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
import type { WorksheetDesignTemplateT } from 'src/contentComponents/common/WorksheetDesign/DesignTemplate.service'
import dateUtils from 'src/utils/DateUtils'

import { COLOR } from '@/constant'
import { getMakeVariantCapabilities } from '@/constant/makeWorksheet/makeVariantSearch'
import {
  getProblemAreaOptionsOverride,
  PROBLEM_AREA_OPTION_FLAGS_OFF,
} from '@/constant/makeWorksheet/problemAreaOptions'
import commonRepo from '@/repository/common.repo'
import type { CoreItem } from '@/service/curriculumCore.service'
import { curriculumCoreService } from '@/service/curriculumCore.service'
import type { PdfDateType } from '@/types/print'

import { DTO } from '../DTO'
import { SchoolSubjectGrade } from '../Student/SchoolSubjectGrade'

export class MakeWorksheetPreviewOptionFilter extends DTO {
  layoutType: WorksheetDesignTemplateT['id'] = 0 // 레이아웃 코드 - 학습지 디자인
  layoutColor: (typeof COLOR)[number]['id'] = 'BLUE' // 학습지 테마색 (BLUE, ORANGE, CRUSTA, PINK, SAPPHIRE, GREEN, JUNGLE_GREEN, GRAY)
  partitionType: PartitionType = 0 // 분할 타입
  wrongAnswerNoteFlag = false // 오답노트 렌더여부
  conceptNameFlag = true // 유형명 렌더여부
  answerRateFlag = true // 정답률 렌더여부
  relationWorkbookFlag = false // 연관교재 정보 렌더 여부
  includeProblemFlag = false // 정답지 렌더 여부
  title = '' // 학습지 제목
  writer = '' // 출제자 이름
  tag: WorksheetCreationBody['tag'] = 'BASIC' // 학습지 태그
  prefix?: string
  conceptSortType: 'FRONT' | 'CHAPTER' = 'CHAPTER' // 개념 분할: FRONT - 학습지 맨앞, CHAPTER - 문제 앞
  schoolType: SchoolType = 'ELEMENTARY'
  // 커리큘럼은 부트에서 비동기로 채워진다. 미인증 진입 등으로 아직 비어 있으면 getAllGrades() 가
  // 빈 배열이라 [0] 이 undefined 이고, 클래스 필드라 여기서 던지면 생성 자체가 실패한다.
  // schoolType 기본값(ELEMENTARY)과 맞는 학년으로 폴백한다.
  grade: CoreItem['gradeSubject'] = curriculumCoreService.getAllGrades()[0]?.gradeSubject ?? '1' // '1' | '2' | '고등수학(상)'...
  revision: Revision = 'CURRICULUM_15' // 개정
  problemPadding = 60
  pdfDateType: PdfDateType = 'TODAY'
  pdfDate: Date | null = new Date() // pdf에 보여지는 유저가 선택한 날짜
  designTemplateId: number = EMPTY_NUMBER_ID // 학습지 만들 때 저장한 디자인템플릿 id
  qrFlag = true // qr코드 렌더 여부
  qrAvailable = true // qr코드 사용 가능 여부
  problemTrendFlag = true // 신경향 라벨 렌더 여부

  constructor(data?: Partial<MakeWorksheetPreviewOptionFilter>) {
    super()

    if (data) {
      Object.assign(this, data)
    }

    // showProblemAreaOptions=false variant: 생성자 데이터·저장값 복원·디자인 템플릿 적용 등
    // 어떤 경로로 플래그가 true가 되어도 상태를 강제 off
    const { showProblemAreaOptions, supportsQr } = getMakeVariantCapabilities(
      globalThis.window?.location.search ?? '',
    )
    if (!showProblemAreaOptions) {
      Object.assign(this, PROBLEM_AREA_OPTION_FLAGS_OFF)
    }
    // supportsQr=false variant: 디자인 템플릿 metadata.qrAvailable가 qrAvailable를 다시 켤 수 있어 봉인 필요
    if (!supportsQr) {
      Object.assign(this, { qrFlag: false, qrAvailable: false })
    }

    makeObservable(this, {
      layoutType: observable,
      layoutColor: observable,
      partitionType: observable,
      wrongAnswerNoteFlag: observable,
      conceptNameFlag: observable,
      answerRateFlag: observable,
      relationWorkbookFlag: observable,
      includeProblemFlag: observable,
      title: observable,
      writer: observable,
      tag: observable,
      prefix: observable,
      conceptSortType: observable,
      schoolType: observable,
      grade: observable,
      revision: observable,
      problemPadding: observable,
      pdfDate: observable,
      pdfDateType: observable,
      designTemplateId: observable,
      qrFlag: observable,
      qrAvailable: observable,
      problemTrendFlag: observable,

      color: computed,
      previewPdfDate: computed,
      createWorksheetParams: computed,
    })

    reaction(
      () => this.wrongAnswerNoteFlag,
      () => {
        if (this.wrongAnswerNoteFlag) {
          runInAction(() => {
            this.partitionType = 0
          })
        }
      },
    )

    reaction(
      () => this.partitionType,
      () => {
        if (this.wrongAnswerNoteFlag) {
          runInAction(() => {
            this.partitionType = 0
          })
        }
      },
    )

    if (!showProblemAreaOptions) {
      reaction(
        () => [
          this.wrongAnswerNoteFlag,
          this.conceptNameFlag,
          this.answerRateFlag,
          this.relationWorkbookFlag,
          this.problemTrendFlag,
        ],
        (flags) => {
          if (flags.some(Boolean)) {
            runInAction(() => {
              Object.assign(this, PROBLEM_AREA_OPTION_FLAGS_OFF)
            })
          }
        },
      )
    }

    if (!supportsQr) {
      reaction(
        () => [this.qrFlag, this.qrAvailable],
        (flags) => {
          if (flags.some(Boolean)) {
            runInAction(() => {
              Object.assign(this, { qrFlag: false, qrAvailable: false })
            })
          }
        },
      )
    }
  }

  static getKoPrefix(tag: MakeWorksheetPreviewOptionFilter['tag']) {
    switch (tag) {
      case 'BASIC':
        return '기본'
      case 'PRACTICE':
        return '연습문제'
      case 'HOMEWORK':
        return '숙제'
      case 'ARITHMETIC':
        return '연산'
      case 'DAILY_TEST':
        return '일일 TEST'
      case 'WEEKLY_TEST':
        return '주간 TEST'
      case 'CHAPTER_TEST':
        return '단원 TEST'
      case 'ENTRANCE_TEST':
        return commonRepo.isSchoolflat ? '진단평가' : '입학 TEST'
      case 'SCHOOL_PREPARE':
        return commonRepo.isSchoolflat ? '교과서 쌍둥이' : '내신대비'
      case 'PRACTICE_TEST':
        return '모의고사'
      case 'PRACTICE_TEST_PAIR':
        return '모의고사쌍둥이'
      case 'CSA_TEST':
        return '수능대비'
      case 'MY_DB_ORIGINAL':
        return '원본'
      case 'SCHOOL_TEST_PAPER':
        return '기출 유사'
      case 'CUSTOM_PAPER':
        return '기타자료 유사'
      case 'CONCEPT_CHIP':
        return '유형별 학습'
      case 'CONCEPT_CHIP_WEAK_PROBLEM':
        return '유형별 오답'
      case 'WEAK_CONCEPT_CHIP':
        return '취약유형'
      case 'WEAK_CONCEPT_CHIP_BY_GROUP':
        return '그룹취약유형'
      case 'GENERAL_TEST':
        return '총괄 TEST'
      case 'WEAK_CHAPTER':
        return '단원별 취약'
      case 'WEAK_DATE':
        return '기간별 오답'
      case 'WEAK_WORKSHEET':
        return '학습지 오답'
      case 'WEAK_ARITHMETIC':
        return '연산 오답'
      case 'WEAK_WORKBOOK':
        return '교재 오답'
      case 'REVIEW':
        return '복습'
      case 'ETC':
        return '직접 입력'
    }
  }

  get previewPdfDate() {
    switch (this.pdfDateType) {
      case 'NONE':
        return null
      case 'TODAY':
        return new Date()
      case 'CUSTOM':
        return this.pdfDate
    }
  }

  get koBasicPrefix() {
    const school = new SchoolSubjectGrade({
      grade: this.grade,
      schoolType: this.schoolType,
      semester: null,
      revision: this.revision,
    })

    switch (this.grade) {
      case '고등수학(상)':
        return '수학(상)'
      case '고등수학(하)':
        return '수학(하)'
      case '확률과 통계':
        return '확통'
      default:
        return school.koSchoolGrade
    }
  }

  get color() {
    const target = COLOR.find(({ id }) => id === this.layoutColor)
    return target?.value || COLOR[0].value
  }

  get createWorksheetParams(): Omit<
    WorksheetCreationBody,
    | 'filterId'
    | 'problemList'
    | 'littleChapterConceptIdList'
    | 'conceptIdList'
    | 'assignStudentIdList'
    | 'shareScope'
  > {
    return {
      title: this.title,
      writer: this.writer,
      ...(this.prefix && { prefix: this.prefix }),
      layoutType: this.layoutType,
      layoutColor: this.layoutColor,
      partitionType: Number(this.partitionType) as PartitionType,
      wrongAnswerNoteFlag: this.wrongAnswerNoteFlag,
      conceptNameFlag: this.conceptNameFlag,
      answerRateFlag: this.answerRateFlag,
      relationWorkbookFlag: this.relationWorkbookFlag,
      includeProblemFlag: this.includeProblemFlag,
      tag: this.tag,
      conceptSortType: this.conceptSortType,
      schoolType: this.schoolType,
      revision: this.revision,
      grade: this.grade,
      problemPadding: this.problemPadding,
      pdfDateType: this.pdfDateType,
      pdfDate:
        this.pdfDateType === 'CUSTOM' && this.previewPdfDate
          ? dateUtils.toDateString(new Date(this.previewPdfDate), '-')
          : null,
      designTemplateId: this.designTemplateId === EMPTY_NUMBER_ID ? null : this.designTemplateId,
      qrFlag: this.qrFlag,
      problemTrendFlag: this.problemTrendFlag,
      // 같은 액션 배치 안에서 update 직후 읽으면 생성자의 클램프 reaction이 아직 실행되기 전일 수 있다
      ...getProblemAreaOptionsOverride(globalThis.window?.location.search ?? ''),
    }
  }

  get createEachStudentWorksheetParams(): Omit<
    GroupWorksheetCreationBody,
    | 'studentWorksheetIdList'
    | 'wrongWorksheetType'
    | 'problemRequestType'
    | 'excludeOOC'
    | 'onlyAutoScorable'
    | 'practiceTest'
    | 'maxProblemCount'
    | 'excludePrevious'
    | 'selectedConceptIdList'
    | 'selectedLittleChapterIdList'
    | 'problemCount'
    | 'level'
    | 'levelWeight'
    | 'problemFilterType'
    | 'minRate'
    | 'maxRate'
    | 'groupKey'
    | 'groupName'
    | 'groupType'
    | 'title'
    | 'writer'
    | 'prefix'
  > &
    // 그룹 학습지 요청은 prefix 가 필수라 호출하는 곳이 태그에 맞는 값으로 채운다
    Partial<Pick<GroupWorksheetCreationBody, 'prefix'>> {
    return {
      ...(this.prefix && { prefix: this.prefix }),
      layoutType: this.layoutType,
      layoutColor: this.layoutColor,
      partitionType: Number(this.partitionType) as PartitionType,
      wrongAnswerNoteFlag: this.wrongAnswerNoteFlag,
      conceptNameFlag: this.conceptNameFlag,
      answerRateFlag: this.answerRateFlag,
      relationWorkbookFlag: this.relationWorkbookFlag,
      includeProblemFlag: this.includeProblemFlag,
      tag: this.tag,
      conceptSortType: this.conceptSortType,
      problemPadding: this.problemPadding,
      pdfDateType: this.pdfDateType,
      qrFlag: this.qrFlag,
    }
  }

  get designOptionParams() {
    return {
      layoutColor: this.layoutColor,
      layoutType: this.layoutType,
      partitionType: this.partitionType,
      wrongAnswerNoteFlag: this.wrongAnswerNoteFlag,
      conceptNameFlag: this.conceptNameFlag,
      answerRateFlag: this.answerRateFlag,
      relationWorkbookFlag: this.relationWorkbookFlag,
      includeProblemFlag: this.includeProblemFlag,
      designTemplateId: this.designTemplateId,
      problemPadding: this.problemPadding,
      pdfDateType: this.pdfDateType,
      pdfDate:
        this.pdfDateType === 'CUSTOM' && this.previewPdfDate
          ? dateUtils.toDateString(new Date(this.previewPdfDate), '-')
          : null,
      qrFlag: this.qrFlag,
      problemTrendFlag: this.problemTrendFlag,
    }
  }
}
